import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronUp, ChevronDown, AlertCircle, ArrowRight, ShoppingCart, TrendingUp, BarChart3, Landmark } from "lucide-react";
import { calculatePartnerIncome, formatGBP, formatVolume, formatNumber } from "@/utils/partnerCalcEngine";
import { usePartnerToolLifecycle } from "@/utils/trackingHooks";

const PARTNER_ITEM_NAME = "ecommerce_partner_calculator";
const PARTNER_ITEM_OPTS = { itemType: "partner_calculator" };

const CONFIG = {
  turnoverBySize: { small: 20000, medium: 50000, large: 100000 },
  cardMix: { debit: 0.40, credit: 0.40, business: 0.20 },
  rateMargins: { debit: 0.0065, credit: 0.0040, business: 0.0030 },
  avgTxnValue: 50,
  authMarginPerTx: 0.015,
  partnerShare: 0.25,
};

const SIZE_LABELS = {
  small: { label: "Small", desc: "around £20,000/month" },
  medium: { label: "Medium", desc: "around £50,000/month" },
  large: { label: "Large", desc: "around £100,000/month" },
};

function ResultCard({ icon: Icon, label, value, variant = "default", testId }) {
  const bg = {
    default: "bg-white border border-brand-light-grey",
    primary: "bg-brand-aqua-light border border-brand-aqua/20",
    annual: "bg-gradient-to-br from-brand-aqua-light to-white border border-brand-aqua/30",
  };
  const textClr = {
    default: "text-brand-black",
    primary: "text-brand-aqua-dark",
    annual: "text-brand-aqua-dark",
  };
  return (
    <div className={`${bg[variant]} rounded-xl p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className="text-brand-grey" />}
        <p className="text-xs font-bold uppercase tracking-wider text-brand-grey">{label}</p>
      </div>
      <p className={`text-xl sm:text-2xl font-heading font-bold ${textClr[variant]}`}>{value}</p>
    </div>
  );
}

function SizeSelector({ value, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Merchant size">
      {Object.entries(SIZE_LABELS).map(([key, meta]) => {
        const selected = value === key;
        return (
          <button
            key={key}
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(key)}
            className={`rounded-xl p-3 text-left border-2 transition-all ${selected ? "border-brand-aqua bg-brand-aqua-light" : "border-brand-light-grey bg-white hover:border-brand-aqua/40 hover:bg-brand-aqua-light/30"}`}
            data-testid={`ecom-size-${key}`}
          >
            <span className={`block text-sm font-bold ${selected ? "text-brand-aqua-dark" : "text-brand-black"}`}>{meta.label}</span>
            <span className="block text-[11px] text-brand-grey mt-0.5 leading-snug">{meta.desc}</span>
          </button>
        );
      })}
    </div>
  );
}

function MerchantStepper({ value, onChange, error }) {
  const numVal = Number(value) || 0;
  const increment = () => onChange(Math.min(numVal + 1, 500));
  const decrement = () => onChange(Math.max(numVal - 1, 1));

  const handleInput = (e) => {
    onChange(e.target.value);
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="number"
            value={value}
            onChange={handleInput}
            min="1"
            max="500"
            step="1"
            aria-label="Number of merchants"
            className={`w-full pl-4 pr-16 py-3 rounded-xl border ${error ? "border-red-400 focus:ring-red-300" : "border-brand-light-grey focus:ring-brand-aqua/30 focus:border-brand-aqua"} bg-white text-brand-black text-sm font-semibold focus:outline-none focus:ring-2 transition-all`}
            data-testid="ecom-merchant-count"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col">
            <button onClick={increment} className="p-0.5 text-brand-grey hover:text-brand-aqua transition-colors" aria-label="Increase" data-testid="ecom-stepper-up"><ChevronUp size={14} /></button>
            <button onClick={decrement} className="p-0.5 text-brand-grey hover:text-brand-aqua transition-colors" aria-label="Decrease" data-testid="ecom-stepper-down"><ChevronDown size={14} /></button>
          </div>
        </div>
      </div>
      {error && (
        <p className="flex items-center gap-1.5 text-xs text-red-500 mt-1.5" data-testid="ecom-validation-error">
          <AlertCircle size={12} />{error}
        </p>
      )}
    </div>
  );
}

function ResultsPanel({ results }) {
  return (
    <div className="space-y-4 pt-2">
      <div className="grid grid-cols-2 gap-3">
        <ResultCard icon={TrendingUp} label="Estimated monthly recurring income" value={formatGBP(results.partnerMonthlyIncome)} variant="primary" testId="ecom-monthly-income" />
        <ResultCard icon={TrendingUp} label="Estimated annual recurring income" value={formatGBP(results.partnerAnnualIncome)} variant="annual" testId="ecom-annual-income" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <ResultCard icon={BarChart3} label="Est. monthly processing volume" value={formatVolume(results.totalVolume)} testId="ecom-total-volume" />
        <ResultCard icon={Landmark} label="Est. monthly gross margin" value={formatGBP(results.totalGrossMargin)} testId="ecom-gross-margin" />
      </div>

      <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
        <span className="text-xs text-brand-grey">Estimated monthly transactions</span>
        <span className="text-sm font-heading font-bold text-brand-black" data-testid="ecom-transaction-count">{formatNumber(results.transactionCount)}</span>
      </div>

      <div className="bg-brand-aqua-light border border-brand-aqua/20 rounded-xl p-4 text-center">
        <p className="text-sm font-semibold text-brand-black">Build recurring revenue from the merchants you already work with.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-1">
        <Link
          to="/partner-programme"
          className="flex-1 bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 transition-colors btn-shine"
          data-testid="ecom-cta-partner"
        >
          Become a partner <ArrowRight size={16} />
        </Link>
        <a
          href="/book"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 bg-white border border-brand-aqua text-brand-aqua-dark font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-brand-aqua-light transition-colors"
          data-testid="ecom-cta-book-call"
        >
          Book a partner call
        </a>
      </div>
    </div>
  );
}

function useEcomPartnerState(defaultMerchantCount, defaultMerchantSize) {
  const [merchantCount, setMerchantCount] = useState(defaultMerchantCount);
  const [merchantSize, setMerchantSize] = useState(defaultMerchantSize);

  const validationError = useMemo(() => {
    if (merchantCount === "" || merchantCount === null || merchantCount === undefined) return null;
    const str = String(merchantCount);
    if (str.includes(".")) return "Please enter a whole number of merchants.";
    const num = Number(merchantCount);
    if (isNaN(num) || num < 1) return "Please enter a valid number of merchants.";
    if (num > 500) return "Maximum 500 merchants.";
    return null;
  }, [merchantCount]);

  const safeCount = useMemo(() => {
    const num = Number(merchantCount);
    if (isNaN(num) || num < 1 || !Number.isInteger(num) || num > 500) return 0;
    return num;
  }, [merchantCount]);

  const results = useMemo(() => {
    if (safeCount === 0) return null;
    return calculatePartnerIncome(safeCount, merchantSize, CONFIG);
  }, [safeCount, merchantSize]);

  return { merchantCount, setMerchantCount, merchantSize, setMerchantSize, validationError, results };
}

export default function EcomPartnerIncomeCalculator({
  defaultMerchantCount = 3,
  defaultMerchantSize = "medium",
  className = "",
  onChange,
}) {
  const { merchantCount, setMerchantCount, merchantSize, setMerchantSize, validationError, results } = useEcomPartnerState(defaultMerchantCount, defaultMerchantSize);
  const { markStarted, markCompleted } = usePartnerToolLifecycle(PARTNER_ITEM_NAME, PARTNER_ITEM_OPTS);

  useEffect(() => {
    if (merchantCount !== defaultMerchantCount || merchantSize !== defaultMerchantSize) markStarted();
  }, [merchantCount, merchantSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (results) markCompleted();
  }, [results]); // eslint-disable-line react-hooks/exhaustive-deps

  // Report the current inputs/results so the page can attach them to a
  // partner enquiry (pass a state setter; called from an effect, never render).
  useEffect(() => {
    if (onChange) onChange({ merchantCount, size: merchantSize, results });
  }, [merchantCount, merchantSize, results]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`bg-white border border-brand-light-grey rounded-2xl shadow-sm overflow-hidden ${className}`} data-testid="ecom-partner-calculator">
      <div className="bg-brand-aqua-light px-6 sm:px-8 py-5 flex items-center gap-3 border-b border-brand-aqua/10">
        <div className="w-10 h-10 rounded-xl bg-brand-aqua flex items-center justify-center">
          <ShoppingCart size={20} className="text-white" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold text-brand-black">Estimate your e-commerce partner income</h3>
          <p className="text-xs text-brand-grey">See how much you could earn by referring online businesses</p>
        </div>
      </div>

      <div className="p-6 sm:p-8 space-y-6">
        <p className="text-sm text-brand-grey leading-relaxed">See how much recurring income you could earn by referring online businesses.</p>

        <div>
          <label className="block text-sm font-semibold text-brand-black mb-1.5">How many merchants could you refer?</label>
          <MerchantStepper value={merchantCount} onChange={setMerchantCount} error={validationError} />
        </div>

        <div>
          <label className="block text-sm font-semibold text-brand-black mb-2">What size are those businesses?</label>
          <SizeSelector value={merchantSize} onChange={setMerchantSize} />
        </div>

        {results && <ResultsPanel results={results} />}

        <p className="text-[11px] text-brand-grey leading-relaxed text-center">
          This calculator provides an illustration based on typical e-commerce merchant assumptions, including card mix, transaction values, and a 25% partner share. Actual earnings may vary depending on merchant behaviour and pricing.
        </p>
      </div>
    </div>
  );
}
